import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Carousel from "../components/Carousel";
import Clients from "../components/Clients";
import Videos from "../components/Videos";
import SEO from "../components/SEO";
import {
  ClientSection,
  FAQSection,
  ReachOutSection,
} from "../components/BottomSection";
import {
  FaRadio,
  FaFilm,
  FaBus,
  FaBullhorn,
  FaLightbulb,
  FaNewspaper,
} from "react-icons/fa6";

import { FaDesktop, FaMapMarkedAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "../lib/axios";

const AdvertisingFAQ = [
  {
    question: "Why Should You Advertise with Indorinama?",
    answer:
      "Indorinama is your one-stop solution for all advertising and branding needs, offering innovative strategies at competitive rates. Our mission is to provide 360-degree advertising solutions that align perfectly with your brand's goals and audience. We specialize in a diverse range of advertising mediums, including: Transit Media, Radio Campaigns, Cinema Advertising, Newspaper Advertising, Non Traditional Advertising, Outdoor advertising",
  },
  {
    question: "How can Indorinama help my business grow through advertising?",
    answer:
      "Our advertising strategies are tailored to your business goals. We use innovative techniques, creative designs, and data-driven digital marketing to enhance your brand visibility, engage your audience, and drive conversions.",
  },
  {
    question: "What industries do you specialize in?",
    answer:
      "Indorinama caters to a variety of industries, including retail, hospitality, healthcare, education, real estate, and more. Our team customizes advertising campaigns to suit the unique needs of your industry.",
  },
];

const Advertising = () => {
  const navigate = useNavigate();

  const categories = [
    {
      name: "Radio Ads",
      slug: "radio",
      icon: FaRadio,
      description:
        "Reach your target audience through strategic radio advertising across major stations.",
      link: "/radio-advertising",
      features: [
        "Wide reach",
        "Local targeting",
        "Cost-effective",
        "High frequency",
      ],
    },
    {
      name: "Transit Ads",
      slug: "transit",
      icon: FaBus,
      description:
        "Mobile advertising solutions on buses, autos, and other transit mediums for maximum visibility.",
      link: "/transit-advertising",
      features: [
        "Mobile visibility",
        "Wide coverage",
        "Cost-effective",
        "Local reach",
      ],
    },
    {
      name: "BTL Ads",
      slug: "btl",
      icon: FaBullhorn,
      description:
        "Below-the-line advertising solutions for targeted marketing campaigns.",
      link: "/btl-advertising",
      features: [
        "Direct engagement",
        "Measurable results",
        "Targeted approach",
        "Cost-effective",
      ],
    },
    {
      name: "Newspaper Ads",
      slug: "newspaper",
      icon: FaNewspaper,
      description:
        "Traditional print advertising in leading newspapers and publications.",
      link: "/newspaper-advertising",
      features: [
        "Credibility",
        "Wide reach",
        "Targeted sections",
        "Flexible sizes",
      ],
    },
    {
      name: "Cinema Ads",
      slug: "cinema",
      icon: FaFilm,
      description:
        "Engage captive audiences with impactful cinema advertising in premium multiplexes.",
      link: "/cinema-advertising",
      features: [
        "Captive audience",
        "High impact",
        "Premium positioning",
        "Brand recall",
      ],
    },
    {
      name: "Outdoor Ads",
      slug: "outdoor",
      icon: FaMapMarkedAlt,
      description:
        "Strategic outdoor advertising solutions including billboards, hoardings, and more.",
      link: "/outdoor-advertising",
      features: [
        "High visibility",
        "24/7 exposure",
        "Location targeting",
        "Brand awareness",
      ],
    },
    {
      name: "Non-Traditional Ads",
      slug: "non_traditional",
      icon: FaLightbulb,
      description:
        "Innovative advertising solutions that break the conventional mold.",
      link: "/non-traditional-advertising",
      features: [
        "Unique approach",
        "High impact",
        "Memorable",
        "Creative solutions",
      ],
    },
    {
      name: "Digital Screen",
      slug: "digital",
      icon: FaDesktop,
      description:
        "Dynamic digital advertising on screens in high-traffic locations.",
      link: "/digital-advertising",
      features: [
        "Real-time updates",
        "Eye-catching displays",
        "Strategic placement",
        "Flexible content",
      ],
    },
  ];

  const waveVariants = {
    hidden: {
      opacity: 0,
      pathLength: 0,
      pathOffset: 1,
    },
    visible: {
      opacity: 1,
      pathLength: 1,
      pathOffset: 0,
      transition: {
        duration: 1.5,
        ease: "easeInOut",
      },
    },
  };

  const [advertisingGlobal, setAdvertisingGlobal] = useState(null);

  const fetchAdvertisingGlobal = async () => {
    try {
      const response = await axios.get("/api/globals/advertising");
      setAdvertisingGlobal(response.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchAdvertisingGlobal();
  }, []);

  return (
    <>
      <SEO
        title="Advertising Services"
        description="Explore comprehensive advertising services including radio, transit, cinema, outdoor, BTL, digital, and newspaper advertising. Get your brand noticed with Indorinama's expert advertising solutions."
        keywords="advertising services, radio advertising, transit advertising, cinema ads, outdoor advertising, digital advertising, newspaper advertising, BTL advertising"
        url="https://indorinama.com/advertising"
      />
      <Navbar />
      <div className="bg-white min-h-screen">
        {/* Banners */}
        <div>
          <Carousel />
        </div>

        {/* How It Works Section */}
        <div className="py-4 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              How Our Advertising Works
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  number: "1",
                  title: "Strategy Development",
                  description:
                    "We analyze your business goals and target audience to create a customized advertising strategy.",
                },
                {
                  number: "2",
                  title: "Creative Execution",
                  description:
                    "Our team develops engaging content and creative materials tailored to your brand.",
                },
                {
                  number: "3",
                  title: "Campaign Management",
                  description:
                    "We handle the complete execution and monitoring of your advertising campaigns.",
                },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  className="text-center p-6 rounded-lg bg-gray-50 transform transition-all hover:scale-105 relative group"
                  whileHover="visible"
                  initial="hidden"
                >
                  {/* Number Circle with Wave Container */}
                  <div className="relative">
                    <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4 relative z-10">
                      <span className="text-2xl text-white">{item.number}</span>
                    </div>

                    {/* Animated Wave using Framer Motion */}
                    <div className="absolute left-0 right-0 top-8 -z-0">
                      <svg
                        className="w-full h-8 overflow-visible"
                        viewBox="0 0 400 20"
                        preserveAspectRatio="none"
                      >
                        <motion.path
                          d="M0,10 C80,30 150,0 200,10 C250,20 320,-10 400,10"
                          stroke="#bc1535"
                          strokeWidth="2"
                          fill="none"
                          strokeLinecap="round"
                          variants={waveVariants}
                        />
                      </svg>
                    </div>
                  </div>

                  <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Categories Section */}
        <div className="py-4">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12 text-brand-red">
              Our Advertising Categories
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {categories.map((category, index) => {
                const Icon = category.icon;
                const adCategoryKey = category.slug === 'outdoor' ? 'outdoors' : category.slug === 'transit' ? 'transition' : category.slug;
                const adImage = advertisingGlobal?.[adCategoryKey]?.at(0)?.image;
                const adImageUrl = adImage?.cloudinary?.secure_url || adImage?.url || "";

                return (
                  <div
                    key={index}
                    className="bg-white rounded-lg shadow-md overflow-hidden cursor-pointer transform transition-all duration-300 hover:scale-105"
                    onClick={() => navigate(category.link)}
                  >
                    <div className="h-48 bg-gray-200 w-full mb-2">
                      {adImageUrl && (
                        <img
                          src={adImageUrl}
                          alt={category.name}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <div className="px-4 pb-4">
                      <div className="flex items-center mb-3">
                        <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mr-4">
                          <Icon className="text-2xl text-red-600" />
                        </div>
                        <h3 className="text-xl font-semibold m-0">
                          {category.name}
                        </h3>
                      </div>
                      <p className="text-gray-600 mb-4">
                        {category.description}
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {category.features.map((feature, i) => (
                          <div
                            key={i}
                            className="flex items-center text-xs text-gray-500 whitespace-nowrap"
                          >
                            <span className="w-1.5 h-1.5 bg-red-600 rounded-full mr-2 shrink-0"></span>
                            <span className="truncate">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Clients */}
        <div className="mt-2">
          <Clients category="advertising" />
        </div>

        {/* Videos */}
        <div className="py-4">
          <div className="container mx-auto px-4">
            <Videos category="advertising" />
          </div>
        </div>

        <FAQSection pageFAQ={AdvertisingFAQ} />
        <ClientSection />

        {/* Why Choose Us Section */}
        <div className="py-4 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12 text-brand-red">
              Why Choose Our Advertising Services
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center p-6">
                <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaBullhorn className="text-2xl text-red-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3">
                  Strategic Approach
                </h3>
                <p className="text-gray-600">
                  Data-driven strategies to maximize your advertising ROI
                </p>
              </div>
              <div className="text-center p-6">
                <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaLightbulb className="text-2xl text-red-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3">
                  Creative Excellence
                </h3>
                <p className="text-gray-600">
                  Innovative ideas that make your brand stand out
                </p>
              </div>
              <div className="text-center p-6">
                <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaMapMarkedAlt className="text-2xl text-red-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Wide Network</h3>
                <p className="text-gray-600">
                  Extensive coverage across multiple advertising channels
                </p>
              </div>
              <div className="text-center p-6">
                <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaFilm className="text-2xl text-red-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Result Focused</h3>
                <p className="text-gray-600">
                  Measurable outcomes and transparent reporting
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ReachOutSection />
      <Footer />
    </>
  );
};

export default Advertising;
